import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useRef } from 'react';
import { api } from '~/lib/api-client';
import { openProviderConnect, type PopupHandle } from '~/lib/nango-connect';
import { connectChecks, connectionFingerprint, type ConnectionRowLike } from '~/lib/connection-fingerprint';
import { notificationKeys } from '~/lib/query-keys';

export interface AvailableProvider {
  uniqueKey: string;
  displayName: string;
  provider: string;
  logo?: string;
  description?: string | null;
  alreadyConnected: boolean;
  capabilityActive: boolean;
  capabilityAvailable: boolean;
  /** Whether an org-level Nango integration exists for this provider at all. */
  configured: boolean;
  /** Connection health, only meaningful when a connection exists. */
  status?: 'connected' | 'expired' | 'error';
  /** The only connection is shared with the whole org: shown read-only (FRONT-2711). */
  sharedByOrg?: boolean;
  /** The connection this card stands for: the one Disconnect removes (FRONT-3180). */
  connectionId?: string;
  /** Per capability: `active`/`available` false means the platform switched it off. */
  capabilities: Array<{ id: string; name: string; toolCount?: number; active?: boolean; available?: boolean }>;
}

export interface ConnectProviderVariables {
  provider: AvailableProvider;
  /** The tab opened synchronously by the caller's click handler (FRONT-985). */
  tab: PopupHandle;
}

const PROVIDERS_KEY = ['providers', 'available'] as const;
const INTEGRATIONS_EXTERNAL_KEY = ['integrations', 'external'] as const;

function invalidateProviderQueries(qc: ReturnType<typeof useQueryClient>) {
  qc.invalidateQueries({ queryKey: PROVIDERS_KEY });
  qc.invalidateQueries({ queryKey: INTEGRATIONS_EXTERNAL_KEY });
}

/**
 * `refetchInterval` (FRONT-1279) lets a caller watch for a connection that is
 * still landing — the provider's webhook can arrive after the sign-in tab has
 * closed, and the 60s `staleTime` would otherwise hide it for a whole minute.
 * Off by default; the Connections page turns it on only while it is waiting on
 * a specific provider.
 */
export function useAvailableProviders(options?: { refetchInterval?: number | false; enabled?: boolean }) {
  return useQuery({
    queryKey: PROVIDERS_KEY,
    enabled: options?.enabled,
    queryFn: async () => {
      const data = await api.get<{ items: AvailableProvider[] }>(
        '/api/admin/integrations/providers/available',
      );
      return data.items ?? [];
    },
    staleTime: 60_000,
    refetchInterval: options?.refetchInterval ?? false,
  });
}

export function useConnectProvider() {
  const qc = useQueryClient();
  // One AbortController per in-flight connect, so `cancel()` always targets
  // the invocation actually running (mutations don't hand this back to us).
  const controllerRef = useRef<AbortController | null>(null);

  const mutation = useMutation({
    mutationFn: async ({ provider, tab }: ConnectProviderVariables) => {
      const controller = new AbortController();
      controllerRef.current = controller;

      let sessionToken: string;
      let connectHost: string;
      // The org's Nango connection-config params (e.g. the Microsoft tenant) —
      // without these the provider's OAuth URL can't be built (FRONT-1204).
      let connectParams: Record<string, string> | undefined;
      try {
        ({ sessionToken, connectHost, connectParams } = await api.post<{
          sessionToken: string;
          connectHost: string;
          connectParams?: Record<string, string>;
        }>('/api/admin/integrations/providers/session-token', {
          providerUniqueKey: provider.uniqueKey,
        }, controller.signal));
      } catch (err) {
        // The tab is already open (opened synchronously by the caller before
        // this mutation ran) — if we can't get a session token, close it
        // rather than leave a stranded blank tab.
        try {
          if (!tab.closed) tab.close();
        } catch {
          /* cross-origin tabs can throw on close; ignore */
        }
        // If the user hit Cancel while this request was in flight, the fetch
        // aborts — surface it as a clean cancellation, not a raw abort error.
        if (controller.signal.aborted) throw new Error('cancelled');
        throw err;
      }

      // FRONT-2363: a reconnect replaces a connection that is already listed,
      // so "connected" alone would pass on the first poll. Snapshot the row
      // first and wait for it to change while the tab is open.
      const readFingerprint = async (): Promise<string | null | undefined> => {
        try {
          const data = await api.get<{ items?: ConnectionRowLike[] } | ConnectionRowLike[]>(
            '/api/admin/integrations/external-tools',
            controller.signal,
          );
          const rows = Array.isArray(data) ? data : data.items ?? [];
          return connectionFingerprint(rows, provider);
        } catch {
          return undefined;
        }
      };
      const reconnecting = provider.alreadyConnected;
      const before = reconnecting ? await readFingerprint() : undefined;
      const isListed = async () => {
        // Resilient poll: a transient error (e.g. a flaky upstream call)
        // must NOT abort the connect flow — just retry on the next tick.
        // Passing the signal lets Cancel interrupt a slow in-flight poll
        // immediately; an aborted fetch throws here and is caught → false,
        // then the loop's own aborted-check turns it into a clean cancel.
        try {
          const data = await api.get<{ items: AvailableProvider[] }>(
            '/api/admin/integrations/providers/available',
            controller.signal,
          );
          return (data.items ?? []).some(
            (p) => p.uniqueKey === provider.uniqueKey && p.alreadyConnected,
          );
        } catch {
          return false;
        }
      };

      // Navigate the pre-opened tab to the provider OAuth (through our proxy,
      // so Nango stays hidden) and wait until the connection is recorded. The
      // integrations service records it from Nango's webhook, so we just poll
      // our own providers list until this one flips to connected — no
      // connectionId or websocket needed on the client.
      await openProviderConnect({
        tab,
        connectHost,
        sessionToken,
        providerUniqueKey: provider.uniqueKey,
        connectParams,
        signal: controller.signal,
        ...connectChecks({ reconnecting, before, readFingerprint, isListed }),
      });
      return provider;
    },
    // FRONT-2363: a completed (re)connect is the one trustworthy signal that
    // a provider is back, so tell the bell — its "<Provider> disconnected"
    // item flips to "is back" with Run now for what was skipped. Best-effort.
    onSuccess: (provider) => {
      api
        .post('/api/notifications/connector-recovered', { provider: provider.uniqueKey })
        .then(() => qc.invalidateQueries({ queryKey: notificationKeys.all }))
        .catch(() => undefined);
    },
    // FRONT-1279: invalidate on SETTLED, not only on success. Every failure
    // verdict here — timeout, tab closed — means "we didn't SEE it land", never
    // "it didn't happen", and the cached list is 60s stale. Refetching once on
    // the way out is what lets the card correct itself instead of showing a
    // provider as unconnected that is in fact connected.
    onSettled: () => {
      controllerRef.current = null;
      invalidateProviderQueries(qc);
    },
  });

  return {
    ...mutation,
    /** Aborts the in-flight connect (if any): stops polling, closes the tab. */
    cancel: () => controllerRef.current?.abort(),
  };
}

export function useDisconnectProvider() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (nangoConnectionId: string) =>
      api.delete<{ ok: true }>(
        '/api/admin/integrations/connections/' + encodeURIComponent(nangoConnectionId),
      ),
    onSuccess: () => invalidateProviderQueries(qc),
  });
}
